import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, Spinner, ErrorState, ImagePlaceholder, PlainText, StatusBadge } from '../components/platform/ui';
import { ProductAction, accessLabel } from '../components/platform/ProductCard';
import RequestForm from '../components/platform/RequestForm';
import useAsync from '../hooks/useAsync';
import { platformApi, errorText } from '../services/platformApi';
import { subscriptionAPI } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import { goToPayment, formatPrice, plural } from '../lib/pay';
import { useLang } from '../lib/lang';

const Faq = ({ items }) => (
  <div className="divide-y divide-black/20 border-y border-black/30">
    {items.map((it, i) => (
      <details key={i} className="group py-3">
        <summary className="flex items-center justify-between cursor-pointer font-bold list-none">
          {it.q}<ChevronDown size={18} className="group-open:rotate-180 transition-transform" />
        </summary>
        <p className="mt-2 text-gray-700 whitespace-pre-line">{it.a}</p>
      </details>
    ))}
  </div>
);

const TariffCards = ({ product, tariffs }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang, to } = useLang();
  const [busyId, setBusyId] = useState(null);
  const base = tariffs.length ? Math.min(...tariffs.map((x) => x.price / x.tokens_count)) : 0;
  const single = tariffs.find((x) => x.tokens_count === 1);

  const buy = async (tariff) => {
    if (!user) { toast.info(t('pl.product.loginToPay')); navigate(to('/login')); return; }
    setBusyId(tariff.id);
    try {
      const res = await subscriptionAPI.createOrder({ subscriptionId: tariff.id, email: user.email });
      toast.info(t('pl.product.redirecting'));
      goToPayment(res.payment);
    } catch (err) {
      setBusyId(null);
      toast.error(errorText(err, t('pl.product.orderError')));
    }
  };

  return (
    <section id="tariffs" className="scroll-mt-24">
      <Heading level={2} className="text-3xl mb-2">{t('pl.product.tariffs')}</Heading>
      <p className="text-gray-700 mb-6">{t('pl.product.tariffsText')}</p>
      <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
        {tariffs.map((tf) => {
          const perUnit = tf.price / tf.tokens_count;
          const saving = single && tf.tokens_count > 1 ? Math.round((1 - perUnit / single.price) * 100) : 0;
          return (
            <Card key={tf.id} className="flex flex-col">
              <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{tf.name}</h3>
              {tf.description && <p className="text-gray-700 mb-3">{tf.description}</p>}
              <p className="text-4xl font-bold text-[#C41E3A] my-3" style={{ fontFamily: 'Playfair Display, serif' }}>{formatPrice(tf.price, lang)}</p>
              <p className="text-gray-700 mb-1">{tf.tokens_count} {plural(tf.tokens_count, t('pl.units.segmentation', { returnObjects: true }))}</p>
              {tf.tokens_count > 1 && (
                <p className="text-sm text-gray-600 mb-4">
                  {t('pl.product.perOne', { price: formatPrice(perUnit, lang) })}{saving > 0 && base ? ` · ${t('pl.product.saving', { percent: saving })}` : ''}
                </p>
              )}
              <div className="mt-auto pt-4">
                <Btn onClick={() => buy(tf)} loading={busyId === tf.id} className="w-full"><CreditCard size={18} />{t('pl.product.pay', { price: formatPrice(tf.price, lang) })}</Btn>
              </div>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-4">{t('pl.product.payNote')}</p>
    </section>
  );
};

const LockedPreview = ({ product }) => {
  const { t } = useTranslation();
  const a = product.access || {};
  if (a.state !== 'locked' && a.state !== 'contact' && a.state !== 'restricted') return null;
  return (
    <div className="relative overflow-hidden border-2 border-black">
      {product.cover_url
        ? <img src={product.cover_url} alt="" className="w-full h-64 object-cover blur-md scale-110" />
        : <ImagePlaceholder className="h-64 blur-sm" />}
      <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-center p-4 gap-3">
        <Lock size={32} />
        <p className="text-lg font-bold">
          {a.state === 'locked' ? t('pl.product.lockedNoTokens')
            : a.state === 'restricted' ? t('pl.product.lockedAdminOnly')
              : (product.price_text || t('pl.product.lockedByAgreement'))}
        </p>
        <ProductAction product={product} />
      </div>
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const { hash } = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { data: p, loading, error, reload } = useAsync(() => platformApi.product(slug), [slug, user?.id, i18n.language]);
  useDocumentTitle(p?.name);

  useEffect(() => {
    if (!p || !hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [p, hash]);

  if (loading) return <SiteLayout><Container className="py-12"><Spinner /></Container></SiteLayout>;
  if (error) {
    const notFound = error.response?.status === 404;
    return <SiteLayout><Container className="py-12"><ErrorState text={notFound ? t('pl.product.notFound') : t('pl.product.loadError')} onRetry={notFound ? undefined : reload} /></Container></SiteLayout>;
  }

  const label = accessLabel(p, t);
  const isPetrovich = p.access_policy === 'user_flag';
  const isCustom = p.product_type === 'custom_service';
  const hasTariffs = p.tariffs && p.tariffs.length > 0;
  const requestKind = isCustom ? 'project' : 'price';
  const gallery = p.gallery?.length ? p.gallery : [{ url: null }, { url: null }, { url: null }];

  return (
    <SiteLayout>
      <Container className="py-10 space-y-14">
        <section className="grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <div className="mb-3"><StatusBadge tone={label.tone}>{label.text}</StatusBadge></div>
            <Heading level={1} className="text-4xl sm:text-5xl mb-4">{p.name}</Heading>
            <p className="text-lg text-gray-700 mb-6">{p.short_description}</p>
            {p.price_text && <p className="text-xl font-bold mb-6">{p.price_text}</p>}
            <div className="flex flex-wrap gap-3">
              <ProductAction product={p} />
              {hasTariffs && p.access?.state !== 'locked' && <Btn variant="secondary" to="#tariffs" onClick={(e) => { e.preventDefault(); document.getElementById('tariffs')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('pl.product.goTariffs')}</Btn>}
            </div>
          </div>
          <div>
            {(p.access?.state === 'locked' || p.access?.state === 'contact' || p.access?.state === 'restricted') ? <LockedPreview product={p} /> : (
              p.cover_url ? <img src={p.cover_url} alt={p.name} className="w-full border-2 border-black" /> : <ImagePlaceholder className="h-64 border-2 border-black" />
            )}
          </div>
        </section>

        {p.full_description && (
          <section>
            <Heading level={2} className="text-3xl mb-4">{t('pl.product.about')}</Heading>
            <PlainText text={p.full_description} className="max-w-3xl text-gray-800" />
          </section>
        )}

        {p.features?.length > 0 && (
          <section>
            <Heading level={2} className="text-3xl mb-4">{t('pl.product.features')}</Heading>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-4xl">
              {p.features.map((f, i) => <li key={i} className="flex gap-2"><Check size={20} className="text-[#C41E3A] shrink-0 mt-0.5" />{f}</li>)}
            </ul>
          </section>
        )}

        {p.workflow?.length > 0 && (
          <section>
            <Heading level={2} className="text-3xl mb-4">{t('pl.product.how')}</Heading>
            <ol className="grid gap-4 md:grid-cols-3">
              {p.workflow.map((s, i) => (
                <Card key={i}><span className="text-sm font-bold text-[#C41E3A]">{t('pl.product.step', { n: i + 1 })}</span><h3 className="text-lg font-bold mb-1">{s.title}</h3><p className="text-gray-700">{s.text}</p></Card>
              ))}
            </ol>
          </section>
        )}

        <section>
          <Heading level={2} className="text-3xl mb-4">{t('pl.product.gallery')}</Heading>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((g, i) => g.url
              ? <figure key={i}><img src={g.url} alt={g.caption || p.name} loading="lazy" className="w-full border-2 border-black" />{g.caption && <figcaption className="text-sm text-gray-600 mt-1">{g.caption}</figcaption>}</figure>
              : <ImagePlaceholder key={i} className="h-40" />)}
          </div>
        </section>

        {hasTariffs && <TariffCards product={p} tariffs={p.tariffs} />}

        {p.access_policy === 'contact' || isPetrovich || isCustom ? (
          <section className="max-w-3xl scroll-mt-24">
            {isPetrovich && p.access?.state === 'granted' ? null : (
              <RequestForm
                productSlug={p.slug}
                kind={requestKind}
                title={isCustom ? t('pl.product.formClinic') : isPetrovich ? t('pl.product.formPetrovich') : t('pl.product.formDefault')}
                submitLabel={isCustom ? t('pl.product.formClinicSubmit') : t('pl.product.formPetrovichSubmit')}
                messageHint={isCustom ? t('pl.product.formClinicHint') : undefined}
              />
            )}
          </section>
        ) : null}

        {p.faq?.length > 0 && (
          <section>
            <Heading level={2} className="text-3xl mb-4">{t('pl.product.faq')}</Heading>
            <div className="max-w-3xl"><Faq items={p.faq} /></div>
          </section>
        )}
      </Container>
    </SiteLayout>
  );
};

export default ProductPage;
